import { useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import { useListaFlutuante } from '../hooks/useListaFlutuante'

interface Props {
  endpoint: string
  codKey: string
  descKey?: string
  tagKey?: string
  codValue: string
  descValue: string
  onSelect: (cod: string, desc: string, item?: Record<string, any>) => void
  placeholder?: string
  buildDesc?: (item: Record<string, any>) => string
  renderItem?: (item: Record<string, any>) => React.ReactNode
  listWidth?: number
  somenteComTexto?: boolean
  // Texto mostrado no campo fechado no lugar de descValue (ex: só o código, pra ocupar
  // menos espaço) — descValue continua valendo pra buscar/onSelect/destaque na lista.
  valorExibido?: string
  disabled?: boolean
  // Parâmetros fixos extras enviados em toda busca (ex: { empresa: empresaCod }).
  extraParams?: Record<string, string | number>
}

export default function LookupSelect({ endpoint, codKey, descKey, tagKey, codValue, descValue, onSelect, placeholder, buildDesc, renderItem, listWidth, somenteComTexto, valorExibido, disabled, extraParams }: Props) {
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [resultados, setResultados] = useState<Record<string, any>[]>([])
  const [buscando, setBuscando] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const descBoxRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { posicao, listaRef } = useListaFlutuante(ref, aberta, () => setAberta(false), listWidth)

  const buscarAgora = useCallback(async (valor: string): Promise<Record<string, any>[]> => {
    if (somenteComTexto && valor.length < 2) return []
    const r = await api.get(endpoint, { params: { ...(extraParams || {}), ...(valor ? { busca: valor } : {}) } })
    return r.data
  }, [endpoint, somenteComTexto, extraParams])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (somenteComTexto && valor.length < 2) { setResultados([]); return }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        setResultados(await buscarAgora(valor))
      } finally {
        setBuscando(false)
      }
    }, 300)
  }, [buscarAgora, somenteComTexto])

  function abrir() {
    if (disabled) return
    setAberta(o => {
      const novo = !o
      if (novo) { setFiltro(''); if (somenteComTexto) setResultados([]); else buscar('') }
      return novo
    })
  }

  function selecionar(item: Record<string, any>) {
    const desc = buildDesc ? buildDesc(item) : String(item[descKey!])
    onSelect(String(item[codKey]), desc, item)
    setAberta(false)
    setFiltro('')
    // O filtro que tinha o foco (dentro do portal) some junto com a lista — sem
    // devolver o foco pra caixa do campo, o navegador perde a referência e o Tab
    // seguinte pula pro primeiro elemento focável da página (o menu lateral) em
    // vez de ir pro próximo campo do formulário.
    setTimeout(() => descBoxRef.current?.focus(), 0)
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div
        ref={descBoxRef}
        className="nat-desc-box"
        onClick={abrir}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={aberta}
        aria-disabled={disabled}
        style={disabled ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : undefined}
        onKeyDown={e => {
          if (disabled) return
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault()
            if (!aberta) abrir()
          } else if (e.key === 'Escape' && aberta) {
            setAberta(false)
          } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            // Digitar direto (sem precisar clicar/Enter pra abrir primeiro) já abre a busca com a letra digitada.
            e.preventDefault()
            setAberta(true)
            setFiltro(e.key)
            buscar(e.key)
          }
        }}
      >
        <span style={{ flexShrink: 0, fontSize: '0.85rem', color: '#1a6ea8', opacity: 0.6 }}>🔍</span>
        {descValue ? (
          <span className="nat-dsc">{valorExibido ?? descValue}</span>
        ) : (
          <span className="nat-placeholder">{placeholder || 'Clique para buscar...'}</span>
        )}
        <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
      </div>

      {aberta && posicao && createPortal(
        <div ref={listaRef} className="nat-lista" style={{ position: 'fixed', ...posicao, minWidth: posicao.width }}>
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Buscar..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
              onKeyDown={async e => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                // Não confia no `resultados` da busca debounced (pode estar defasado se o
                // Enter vier rápido demais) — busca de novo na hora antes de decidir.
                if (debounceRef.current) clearTimeout(debounceRef.current)
                setBuscando(true)
                try {
                  const r = await buscarAgora(filtro)
                  setResultados(r)
                  // Só decide sozinho se não houver ambiguidade — o mesmo código pode
                  // aparecer em mais de uma linha com descrição diferente (ex: CFOP 1556).
                  const exatos = r.filter(item => String(item[codKey]).toUpperCase() === filtro.trim().toUpperCase())
                  const alvo = exatos.length === 1 ? exatos[0] : (r.length === 1 ? r[0] : null)
                  if (alvo) selecionar(alvo)
                } finally {
                  setBuscando(false)
                }
              }}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && resultados.length === 0 && somenteComTexto && filtro.length < 2 && (
              <div className="nat-vazio">Digite o CNPJ ou o nome para buscar...</div>
            )}
            {!buscando && resultados.length === 0 && !(somenteComTexto && filtro.length < 2) && (
              <div className="nat-vazio">Nenhum resultado</div>
            )}
            {resultados.map((item, idx) => (
              <button
                key={`${item[codKey]}-${idx}`}
                className={`nat-item${String(item[codKey]) === codValue && (!descKey || String(item[descKey]) === descValue) ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(item)}
              >
                {renderItem ? renderItem(item) : (
                  <>
                    {tagKey && item[tagKey] && (
                      <div className="nat-item-linha1">
                        <span className="nat-item-cfop">{item[tagKey]}</span>
                      </div>
                    )}
                    <div className="nat-item-dsc">{item[descKey!]}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
